import logger from "../utils/logger";
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toPng } from 'html-to-image';
import API_URL from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '../components/ui/dialog';
import { TrendingUp, DollarSign, Users, Target, Award, ChevronLeft, ChevronRight, Plus, Trash2, AlertTriangle, FileText, Copy, CheckCircle2, Activity, LogIn, MapPin, UserPlus, Flag, IndianRupee, GraduationCap, ShieldCheck, Trophy, Flame, Crown, Download, Share2, Calendar } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const COLORS = ['#2563eb', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const SalaryBenchmarkCard = ({ ladder }) => (
    <Card className="rounded-none border-slate-200 border-l-4 border-l-brand bg-gradient-to-br from-blue-50 to-white" data-testid="salary-benchmark-card">
        <CardHeader className="border-b border-slate-200 py-3">
            <CardTitle className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                <Target className="h-4 w-4 text-brand" /> Salary Benchmark Model
            </CardTitle>
            <p className="text-[11px] text-slate-500 mt-1">Every RM must generate revenue at minimum <span className="font-bold text-brand">2× their salary</span> to justify their cost. This is the baseline — not the ceiling.</p>
        </CardHeader>
        <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <p className="text-[10px] font-bold uppercase text-slate-400 mb-2">Example Benchmark</p>
                    <div className="bg-white border border-slate-200 p-3 grid grid-cols-2 gap-3">
                        <div><p className="text-[10px] uppercase text-slate-500">Salary</p><p className="text-lg font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Rs.33,000<span className="text-xs font-normal text-slate-400">/mo</span></p></div>
                        <div><p className="text-[10px] uppercase text-slate-500">Required (200%)</p><p className="text-lg font-black text-brand" style={{ fontFamily: 'Chivo, sans-serif' }}>Rs.66,000<span className="text-xs font-normal text-slate-400">/mo</span></p></div>
                    </div>
                    <div className="bg-amber-50 border border-amber-200 p-2 mt-2 flex items-start gap-2">
                        <AlertTriangle className="h-3.5 w-3.5 text-amber-700 mt-0.5 flex-shrink-0" />
                        <p className="text-[11px] text-amber-800">Falling below 200% triggers a <span className="font-bold">performance review</span> and field-monitoring protocol.</p>
                    </div>
                </div>
                <div>
                    <p className="text-[10px] font-bold uppercase text-slate-400 mb-2">Incentive Ladder — Earn More, Do More</p>
                    <div className="space-y-1">
                        {(ladder && ladder.length > 0 ? ladder : []).slice().sort((a,b) => a.min_percentage - b.min_percentage).map((s, i) => (
                            <div key={`bench-slab-${i}`} className="bg-white border border-slate-200 p-2 flex items-center justify-between">
                                <span className="text-xs font-medium text-slate-700">{s.min_percentage}% — {s.max_percentage > 0 ? `${s.max_percentage}%` : 'Unlimited'}</span>
                                <Badge className="rounded-none bg-emerald-100 text-emerald-700 font-bold text-[10px]">{s.incentive_percentage}% incentive</Badge>
                            </div>
                        ))}
                        {(!ladder || ladder.length === 0) && <p className="text-xs text-slate-400 italic">No slabs configured. Use "Reset to Default Ladder" on the Incentive Slabs tab.</p>}
                    </div>
                </div>
            </div>
        </CardContent>
    </Card>
);


const CRMDashboard = () => {
    const { canManage } = useAuth();
    const [dashboard, setDashboard] = useState(null);
    const [justification, setJustification] = useState(null);
    const [kpiData, setKpiData] = useState(null);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('overview');
    const [slabDialog, setSlabDialog] = useState(false);
    const [slabForm, setSlabForm] = useState({ min_percentage: '', max_percentage: '', incentive_percentage: '', description: '' });
    const [dailyReportDialog, setDailyReportDialog] = useState(null);
    const [dailyReportText, setDailyReportText] = useState('');
    const [dailyReportLoading, setDailyReportLoading] = useState(false);
    const [copiedReport, setCopiedReport] = useState(false);
    const [timelineDialog, setTimelineDialog] = useState(null);
    const [timelineData, setTimelineData] = useState(null);
    const [timelineLoading, setTimelineLoading] = useState(false);
    const [timelineDays, setTimelineDays] = useState(14);
    const [coachDialog, setCoachDialog] = useState(null);
    const [coachForm, setCoachForm] = useState({ session_type: 'timeline_review', notes: '', start_catchup: false, catchup_days: 7 });
    const [coachSaving, setCoachSaving] = useState(false);
    const [alertsFilter, setAlertsFilter] = useState('uncoached');
    const [leaderboard, setLeaderboard] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [shareSettings, setShareSettings] = useState(null);
    const [shareSaving, setShareSaving] = useState(false);
    const [shareDigestSending, setShareDigestSending] = useState(false);
    const [previewHtml, setPreviewHtml] = useState(null);
    const [previewSubject, setPreviewSubject] = useState('');
    const [previewLoading, setPreviewLoading] = useState(false);
    const [leaderboardVertical, setLeaderboardVertical] = useState('all');
    const [leaderboardLoading, setLeaderboardLoading] = useState(false);
    const [crmConfig, setCrmConfig] = useState({ working_days_per_month: 25 });
    const [crmConfigSaving, setCrmConfigSaving] = useState(false);
    const [catchupAudit, setCatchupAudit] = useState(null);
    const [catchupAuditLoading, setCatchupAuditLoading] = useState(false);
    const [catchupAuditDays, setCatchupAuditDays] = useState(90);
    const [catchupAuditStatus, setCatchupAuditStatus] = useState('all');
    // Catalog (admin Products + Categories)
    const [catalogData, setCatalogData] = useState({ products: [], categories: [] });
    const [catalogLoading, setCatalogLoading] = useState(false);
    const [productForm, setProductForm] = useState({ name: '', category: '', commission_rate: '', description: '' });
    const [categoryForm, setCategoryForm] = useState({ name: '', description: '' });
    const [productDialog, setProductDialog] = useState(false);
    const [categoryDialog, setCategoryDialog] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const leaderboardRef = useRef(null);

    const m = currentDate.getMonth() + 1;
    const y = currentDate.getFullYear();

    const fetchData = useCallback(async () => {
        try {
            const requests = [axios.get(`${API_URL}/api/crm/revenue-dashboard`, { withCredentials: true })];
            if (canManage) {
                requests.push(axios.get(`${API_URL}/api/crm/salary-justification?month=${m}&year=${y}`, { withCredentials: true }));
                requests.push(axios.get(`${API_URL}/api/crm/rm-kpi?month=${m}&year=${y}`, { withCredentials: true }));
                requests.push(axios.get(`${API_URL}/api/crm/leaderboard?month=${m}&year=${y}`, { withCredentials: true }));
            }
            const results = await Promise.all(requests);
            setDashboard(results[0].data);
            if (results[1]) setJustification(results[1].data);
            if (results[2]) setKpiData(results[2].data);
            if (results[3]) setLeaderboard(results[3].data);
        } catch (error) { logger.error('CRM Dashboard:', error); }
        finally { setLoading(false); }
    }, [m, y, canManage]);

    useEffect(() => { fetchData(); }, [fetchData]);

    // Refetch leaderboard when vertical filter changes
    const fetchLeaderboardByVertical = useCallback(async (vertical) => {
        if (!canManage) return;
        setLeaderboardLoading(true);
        try {
            const url = vertical && vertical !== 'all'
                ? `${API_URL}/api/crm/leaderboard?month=${m}&year=${y}&vertical=${encodeURIComponent(vertical)}`
                : `${API_URL}/api/crm/leaderboard?month=${m}&year=${y}`;
            const { data } = await axios.get(url, { withCredentials: true });
            setLeaderboard(data);
        } catch (error) { logger.error('Leaderboard vertical fetch', error); }
        finally { setLeaderboardLoading(false); }
    }, [m, y, canManage]);

    // ============== CATALOG (Admin: Products + Categories) ==============
    const fetchCatalog = useCallback(async () => {
        if (!canManage) return;
        setCatalogLoading(true);
        try {
            const [pr, cr] = await Promise.all([
                axios.get(`${API_URL}/api/crm/products`, { withCredentials: true }),
                axios.get(`${API_URL}/api/crm/categories`, { withCredentials: true }),
            ]);
            setCatalogData({ products: pr.data, categories: cr.data });
        } catch (error) { logger.error('catalog fetch', error); }
        finally { setCatalogLoading(false); }
    }, [canManage]);

    const saveProduct = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                name: productForm.name.trim(),
                category: productForm.category || null,
                commission_rate: parseFloat(productForm.commission_rate) || 0,
                description: productForm.description || '',
            };
            if (editingProduct) {
                await axios.put(`${API_URL}/api/crm/products/${editingProduct}`, payload, { withCredentials: true });
            } else {
                await axios.post(`${API_URL}/api/crm/products`, payload, { withCredentials: true });
            }
            setProductDialog(false);
            setEditingProduct(null);
            setProductForm({ name: '', category: '', commission_rate: '', description: '' });
            fetchCatalog();
        } catch (error) { alert(error.response?.data?.detail || 'Error saving product'); }
    };

    const saveCategory = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_URL}/api/crm/categories`, {
                name: categoryForm.name.trim(),
                description: categoryForm.description || '',
            }, { withCredentials: true });
            setCategoryDialog(false);
            setCategoryForm({ name: '', description: '' });
            fetchCatalog();
        } catch (error) { alert(error.response?.data?.detail || 'Error saving category'); }
    };

    const editProduct = (p) => {
        setEditingProduct(p._id);
        setProductForm({
            name: p.name || '',
            category: p.category || '',
            commission_rate: p.commission_rate ?? '',
            description: p.description || '',
        });
        setProductDialog(true);
    };
    // ============== /CATALOG ==============

    const createSlab = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${API_URL}/api/crm/incentive-slabs`, {
                ...slabForm, min_percentage: parseFloat(slabForm.min_percentage), max_percentage: parseFloat(slabForm.max_percentage) || 0, incentive_percentage: parseFloat(slabForm.incentive_percentage)
            }, { withCredentials: true });
            setSlabDialog(false);
            setSlabForm({ min_percentage: '', max_percentage: '', incentive_percentage: '', description: '' });
            fetchData();
        } catch (error) { alert(error.response?.data?.detail || 'Error'); }
    };

    const deleteSlab = async (id) => {
        try { await axios.delete(`${API_URL}/api/crm/incentive-slabs/${id}`, { withCredentials: true }); fetchData(); }
        catch (error) { alert('Error'); }
    };

    const openDailyReport = async (rm) => {
        setDailyReportDialog(rm);
        setDailyReportLoading(true);
        setCopiedReport(false);
        try {
            const { data } = await axios.get(`${API_URL}/api/crm/daily-report/${rm.rm_id}`, { withCredentials: true });
            setDailyReportText(data.report_text);
        } catch (error) { setDailyReportText('Error loading report'); logger.error('daily report', error); }
        finally { setDailyReportLoading(false); }
    };

    const copyReport = async () => {
        try {
            await navigator.clipboard.writeText(dailyReportText);
            setCopiedReport(true);
            setTimeout(() => setCopiedReport(false), 2000);
        } catch { alert('Copy failed. Select text manually.'); }
    };

    const sendWhatsApp = () => {
        const url = `https://wa.me/?text=${encodeURIComponent(dailyReportText)}`;
        window.open(url, '_blank');
    };

    const openTimeline = useCallback(async (rm, days = 14) => {
        setTimelineDialog(rm);
        setTimelineLoading(true);
        setTimelineDays(days);
        try {
            const { data } = await axios.get(`${API_URL}/api/crm/rm-timeline/${rm.rm_id}?days=${days}`, { withCredentials: true });
            setTimelineData(data);
        } catch (error) { logger.error('timeline', error); setTimelineData(null); }
        finally { setTimelineLoading(false); }
    }, []);

    const timelineIcon = (type) => {
        const common = "h-4 w-4";
        switch (type) {
            case 'login': return <LogIn className={`${common} text-blue-600`} />;
            case 'visit': return <MapPin className={`${common} text-indigo-600`} />;
            case 'lead_created': return <UserPlus className={`${common} text-amber-600`} />;
            case 'lead_status': return <Flag className={`${common} text-purple-600`} />;
            case 'revenue': return <IndianRupee className={`${common} text-green-700`} />;
            default: return <Activity className={`${common} text-slate-500`} />;
        }
    };

    const timelineDotColor = (type) => ({
        login: 'bg-blue-500', visit: 'bg-indigo-500', lead_created: 'bg-amber-500',
        lead_status: 'bg-purple-500', revenue: 'bg-green-600'
    }[type] || 'bg-slate-400');

    const openCoach = (rm) => {
        setCoachDialog(rm);
        setCoachForm({ session_type: 'timeline_review', notes: '', start_catchup: false, catchup_days: 7 });
    };

    const saveCoaching = async () => {
        if (!coachDialog) return;
        setCoachSaving(true);
        try {
            const coachRes = await axios.post(`${API_URL}/api/crm/rm-coaching`, {
                rm_id: coachDialog.rm_id,
                session_type: coachForm.session_type,
                notes: coachForm.notes,
            }, { withCredentials: true });
            if (coachForm.start_catchup) {
                await axios.post(`${API_URL}/api/crm/rm-catchup`, {
                    rm_id: coachDialog.rm_id,
                    days: parseInt(coachForm.catchup_days) || 7,
                    daily_multiplier: 2.0,
                    target_justification_pct: 200,
                    coaching_id: coachRes.data.id,
                    notes: `Catch-up after ${coachForm.session_type} session`,
                }, { withCredentials: true });
            }
            setCoachDialog(null);
            setTimelineDialog(null);
            await fetchData();
        } catch (error) { alert(error.response?.data?.detail || 'Error'); logger.error('coach', error); }
        finally { setCoachSaving(false); }
    };

    const formatCoachedDate = (iso) => {
        if (!iso) return '';
        try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
        catch { return iso.slice(0, 10); }
    };

    const fetchShareSettings = useCallback(async () => {
        if (!canManage) return;
        try {
            const { data } = await axios.get(`${API_URL}/api/crm/share-settings`, { withCredentials: true });
            setShareSettings(data);
        } catch (error) { logger.error('share-settings', error); }
    }, [canManage]);

    const fetchCrmConfig = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/crm/config`, { withCredentials: true });
            setCrmConfig({ working_days_per_month: data.working_days_per_month || 25 });
        } catch (error) { logger.error('crm-config', error); }
    }, []);

    const fetchCatchupAudit = useCallback(async () => {
        if (!canManage) return;
        setCatchupAuditLoading(true);
        try {
            const params = new URLSearchParams({ days: String(catchupAuditDays) });
            if (catchupAuditStatus && catchupAuditStatus !== 'all') params.append('status', catchupAuditStatus);
            const { data } = await axios.get(`${API_URL}/api/crm/rm-catchup/history?${params.toString()}`, { withCredentials: true });
            setCatchupAudit(data);
        } catch (error) { logger.error('catchup-audit', error); }
        finally { setCatchupAuditLoading(false); }
    }, [canManage, catchupAuditDays, catchupAuditStatus]);

    const saveCrmConfig = async () => {
        const wd = parseInt(crmConfig.working_days_per_month, 10);
        if (Number.isNaN(wd) || wd < 18 || wd > 31) {
            alert('Working days per month must be a number between 18 and 31');
            return;
        }
        setCrmConfigSaving(true);
        try {
            await axios.put(`${API_URL}/api/crm/config`, { working_days_per_month: wd }, { withCredentials: true });
            alert(`Working days per month updated to ${wd}. Today's Plan pacing math will use this value.`);
        } catch (error) { alert(error.response?.data?.detail || 'Save failed'); logger.error('save crm-config', error); }
        finally { setCrmConfigSaving(false); }
    };

    const fetchPreview = useCallback(async () => {
        if (!canManage) return;
        setPreviewLoading(true);
        try {
            const { data } = await axios.get(`${API_URL}/api/crm/share-settings/preview`, { withCredentials: true });
            setPreviewHtml(data.html);
            setPreviewSubject(data.subject);
        } catch (error) { logger.error('preview', error); }
        finally { setPreviewLoading(false); }
    }, [canManage]);

    useEffect(() => {
        if (tab === 'auto-share') {
            fetchShareSettings();
            fetchPreview();
            fetchCrmConfig();
        }
    }, [tab, fetchShareSettings, fetchPreview, fetchCrmConfig]);

    useEffect(() => {
        if (tab === 'catchup-audit') {
            fetchCatchupAudit();
        }
    }, [tab, fetchCatchupAudit]);

    useEffect(() => {
        if (tab === 'catalog') {
            fetchCatalog();
        }
    }, [tab, fetchCatalog]);

    const updateShareField = (key, value) => setShareSettings({ ...shareSettings, [key]: value });

    const saveShareSettings = async () => {
        if (!shareSettings) return;
        setShareSaving(true);
        try {
            const payload = { ...shareSettings };
            ['next_run_at', 'last_sent_at', 'last_send_status', 'whatsapp_api_key_masked', 'updated_at'].forEach(k => delete payload[k]);
            const { data } = await axios.put(`${API_URL}/api/crm/share-settings`, payload, { withCredentials: true });
            setShareSettings(data.settings);
            alert('Schedule saved' + (data.settings.next_run_at ? ` — next run ${new Date(data.settings.next_run_at).toLocaleString()}` : ''));
        } catch (error) { alert(error.response?.data?.detail || 'Save failed'); logger.error('save share', error); }
        finally { setShareSaving(false); }
    };

    const sendDigestNow = async () => {
        if (!window.confirm('Send the leaderboard digest to all configured recipients now?')) return;
        setShareDigestSending(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/crm/share-settings/send-now`, {}, { withCredentials: true });
            alert(`${data.message}${data.errors?.length ? '\nFailed: ' + data.errors.join(', ') : ''}`);
            fetchShareSettings();
        } catch (error) { alert(error.response?.data?.detail || 'Send failed'); }
        finally { setShareDigestSending(false); }
    };

    const sendDigestToMe = async () => {
        setShareDigestSending(true);
        try {
            const { data } = await axios.post(`${API_URL}/api/crm/share-settings/send-to-me`, {}, { withCredentials: true });
            alert(data.message || 'Preview email sent to you');
        } catch (error) { alert(error.response?.data?.detail || 'Send failed'); }
        finally { setShareDigestSending(false); }
    };

    const exportLeaderboardImage = async (action = 'download') => {
        if (!leaderboardRef.current) return;
        setExporting(true);
        try {
            const dataUrl = await toPng(leaderboardRef.current, {
                cacheBust: true,
                backgroundColor: '#ffffff',
                pixelRatio: 2,
                style: { fontFamily: 'Inter, sans-serif' },
            });
            const filename = `digi-loans-leaderboard-${MONTHS[m - 1]}-${y}.png`;
            if (action === 'download') {
                const link = document.createElement('a');
                link.download = filename;
                link.href = dataUrl;
                link.click();
            } else if (action === 'whatsapp') {
                // Convert dataURL to blob and copy to clipboard so user can paste in WhatsApp
                try {
                    const blob = await (await fetch(dataUrl)).blob();
                    if (navigator.clipboard && window.ClipboardItem) {
                        await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
                        alert('Leaderboard image copied to clipboard. Paste it into your WhatsApp group chat.');
                    } else {
                        // Fallback: download + open WhatsApp Web
                        const link = document.createElement('a');
                        link.download = filename;
                        link.href = dataUrl;
                        link.click();
                        window.open('https://web.whatsapp.com/', '_blank');
                    }
                } catch {
                    const link = document.createElement('a');
                    link.download = filename;
                    link.href = dataUrl;
                    link.click();
                }
            }
        } catch (error) { alert('Export failed: ' + error.message); logger.error('export', error); }
        finally { setExporting(false); }
    };

    const resetSlabsToDefault = async () => {
        if (!window.confirm('This will replace ALL existing incentive slabs with the 5-tier default ladder (200/250/300/350/400). Continue?')) return;
        try {
            await axios.post(`${API_URL}/api/crm/incentive-slabs/reset-defaults`, {}, { withCredentials: true });
            await fetchData();
        } catch (error) { alert(error.response?.data?.detail || 'Error'); logger.error('reset slabs', error); }
    };

    const benchmarkRevenue = (salary) => Math.round((salary || 0) * 2);

    const perfBadge = (cat) => {
        const map = {
            'Top Performer': 'bg-emerald-100 text-emerald-700',
            'Strong RM': 'bg-blue-100 text-blue-700',
            'Average': 'bg-amber-100 text-amber-700',
            'Low Performer': 'bg-red-100 text-red-700',
        };
        return map[cat] || 'bg-slate-100 text-slate-700';
    };

    if (loading) return <div className="flex items-center justify-center h-64"><div className="text-slate-500">Loading...</div></div>;

    const d = dashboard;
    const totals = d?.totals || {};

    return (
        <div className="space-y-6" data-testid="crm-dashboard-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>CRM Dashboard</h1>
                    <p className="text-sm text-slate-500 mt-1">Revenue analytics, salary justification & incentives</p>
                </div>
                <div className="flex flex-wrap bg-white rounded-xl border border-slate-200 p-1 w-fit shadow-sm">
                    <Button variant={tab === 'overview' ? 'default' : 'ghost'} size="sm" className="rounded-lg" onClick={() => setTab('overview')} data-testid="tab-overview">Revenue</Button>
                    {canManage && <Button variant={tab === 'kpi' ? 'default' : 'ghost'} size="sm" className="rounded-lg" onClick={() => setTab('kpi')} data-testid="tab-kpi">RM KPIs</Button>}
                    {canManage && <Button variant={tab === 'leaderboard' ? 'default' : 'ghost'} size="sm" className="rounded-lg" onClick={() => setTab('leaderboard')} data-testid="tab-leaderboard">Leaderboard</Button>}
                    {canManage && <Button variant={tab === 'alerts' ? 'default' : 'ghost'} size="sm" className="rounded-lg" onClick={() => setTab('alerts')} data-testid="tab-alerts">Alerts</Button>}
                    {canManage && <Button variant={tab === 'justification' ? 'default' : 'ghost'} size="sm" className="rounded-lg" onClick={() => setTab('justification')} data-testid="tab-justification">Salary Justification</Button>}
                    {canManage && <Button variant={tab === 'incentives' ? 'default' : 'ghost'} size="sm" className="rounded-lg" onClick={() => setTab('incentives')} data-testid="tab-incentives">Incentive Slabs</Button>}
                    {canManage && <Button variant={tab === 'auto-share' ? 'default' : 'ghost'} size="sm" className="rounded-lg" onClick={() => setTab('auto-share')} data-testid="tab-auto-share">Auto-Share</Button>}
                    {canManage && <Button variant={tab === 'catchup-audit' ? 'default' : 'ghost'} size="sm" className="rounded-lg" onClick={() => setTab('catchup-audit')} data-testid="tab-catchup-audit">Catch-up Audit</Button>}
                    {canManage && <Button variant={tab === 'catalog' ? 'default' : 'ghost'} size="sm" className="rounded-lg" onClick={() => setTab('catalog')} data-testid="tab-catalog">Catalog</Button>}
                </div>
            </div>

            {tab === 'overview' && d && (
                <>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <Card className="rounded-none border-slate-200"><CardContent className="p-5"><p className="text-[10px] font-bold uppercase text-slate-400">Total Leads</p><p className="text-2xl font-black text-slate-900 mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>{d.total_leads}</p></CardContent></Card>
                        <Card className="rounded-none border-slate-200"><CardContent className="p-5"><p className="text-[10px] font-bold uppercase text-slate-400">Disbursed</p><p className="text-2xl font-black text-green-600 mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>{d.disbursed}</p></CardContent></Card>
                        <Card className="rounded-none border-slate-200"><CardContent className="p-5"><p className="text-[10px] font-bold uppercase text-slate-400">Gross Revenue</p><p className="text-2xl font-black text-brand mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>Rs.{(totals.gross || 0).toLocaleString()}</p></CardContent></Card>
                        <Card className="rounded-none border-slate-200"><CardContent className="p-5"><p className="text-[10px] font-bold uppercase text-slate-400">Net Revenue</p><p className="text-2xl font-black text-green-700 mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>Rs.{(totals.net || 0).toLocaleString()}</p></CardContent></Card>
                    </div>

                    {/* Lead Funnel */}
                    <Card className="rounded-none border-slate-200" data-testid="lead-funnel">
                        <CardHeader className="border-b border-slate-200 py-3"><CardTitle className="text-sm font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Lead Pipeline Funnel</CardTitle></CardHeader>
                        <CardContent className="p-4">
                            <div className="flex flex-wrap gap-2">
                                {Object.entries(d.funnel || {}).map(([status, count]) => (
                                    <div key={status} className="flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 text-sm">
                                        <span className="text-slate-500 capitalize">{status.replace('_', ' ')}</span>
                                        <span className="font-black text-slate-900">{count}</span>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Monthly Trend */}
                        <Card className="rounded-none border-slate-200"><CardHeader className="border-b border-slate-200 py-3"><CardTitle className="text-sm font-bold flex items-center gap-2"><TrendingUp className="h-4 w-4 text-brand" />Monthly Revenue</CardTitle></CardHeader>
                            <CardContent className="p-4">{d.monthly_trend?.length > 0 ? (
                                <ResponsiveContainer width="100%" height={250}><BarChart data={d.monthly_trend}><CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" /><XAxis dataKey="month" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip contentStyle={{ border: '1px solid #e2e8f0', borderRadius: 0 }} /><Bar dataKey="gross" fill="#94a3b8" name="Gross" /><Bar dataKey="net" fill="#2563eb" name="Net" /></BarChart></ResponsiveContainer>
                            ) : <div className="h-[250px] flex items-center justify-center text-slate-400">No revenue data yet</div>}</CardContent></Card>

                        {/* Category Revenue */}
                        <Card className="rounded-none border-slate-200"><CardHeader className="border-b border-slate-200 py-3"><CardTitle className="text-sm font-bold">Category-wise Revenue</CardTitle></CardHeader>
                            <CardContent className="p-4">{d.category_revenue?.length > 0 ? (
                                <ResponsiveContainer width="100%" height={250}><PieChart><Pie data={d.category_revenue.map(c => ({ name: c._id || 'Uncategorized', value: c.net }))} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}>
                                    {d.category_revenue.map((_, i) => <Cell key={`cat-${i}`} fill={COLORS[i % COLORS.length]} />)}
                                </Pie><Tooltip /></PieChart></ResponsiveContainer>
                            ) : <div className="h-[250px] flex items-center justify-center text-slate-400">No category data</div>}</CardContent></Card>
                    </div>
                </>
            )}

            {tab === 'kpi' && kpiData && (
                <>
                    <SalaryBenchmarkCard ladder={justification?.slabs} />
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="sm" className="rounded-none" onClick={() => setCurrentDate(new Date(y, m - 2, 1))} data-testid="kpi-prev-month"><ChevronLeft className="h-4 w-4" /></Button>
                        <span className="text-sm font-bold text-slate-700">{MONTHS[m - 1]} {y}</span>
                        <Button variant="ghost" size="sm" className="rounded-none" onClick={() => setCurrentDate(new Date(y, m, 1))} data-testid="kpi-next-month"><ChevronRight className="h-4 w-4" /></Button>
                    </div>

                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <Card className="relative rounded-xl border-slate-200 bg-gradient-to-br from-emerald-50/40 to-white overflow-hidden shadow-sm"><div className="absolute top-0 left-0 h-1 w-full bg-emerald-500" /><CardContent className="p-5"><p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">Top Performers</p><p className="text-3xl font-black text-emerald-700 mt-1.5 tabular-nums leading-none" style={{ fontFamily: 'Chivo, sans-serif' }}>{kpiData.summary.top_performers}</p><p className="text-[10px] text-slate-500 mt-1.5">Rs.3Cr+ Net Rev</p></CardContent></Card>
                        <Card className="relative rounded-xl border-slate-200 bg-gradient-to-br from-blue-50/40 to-white overflow-hidden shadow-sm"><div className="absolute top-0 left-0 h-1 w-full bg-blue-500" /><CardContent className="p-5"><p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">Strong RMs</p><p className="text-3xl font-black text-blue-700 mt-1.5 tabular-nums leading-none" style={{ fontFamily: 'Chivo, sans-serif' }}>{kpiData.summary.strong_rms}</p><p className="text-[10px] text-slate-500 mt-1.5">Rs.2-3Cr</p></CardContent></Card>
                        <Card className="relative rounded-xl border-slate-200 bg-gradient-to-br from-amber-50/40 to-white overflow-hidden shadow-sm"><div className="absolute top-0 left-0 h-1 w-full bg-amber-500" /><CardContent className="p-5"><p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">Average</p><p className="text-3xl font-black text-amber-600 mt-1.5 tabular-nums leading-none" style={{ fontFamily: 'Chivo, sans-serif' }}>{kpiData.summary.average}</p><p className="text-[10px] text-slate-500 mt-1.5">Rs.1-2Cr</p></CardContent></Card>
                        <Card className="relative rounded-xl border-slate-200 bg-gradient-to-br from-red-50/40 to-white overflow-hidden shadow-sm"><div className="absolute top-0 left-0 h-1 w-full bg-red-500" /><CardContent className="p-5"><p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">Low Performers</p><p className="text-3xl font-black text-red-600 mt-1.5 tabular-nums leading-none" style={{ fontFamily: 'Chivo, sans-serif' }}>{kpiData.summary.low_performers}</p><p className="text-[10px] text-slate-500 mt-1.5">Need action</p></CardContent></Card>
                    </div>

                    {/* Level 1-4 KPI Table */}
                    <Card className="rounded-xl border-slate-200 shadow-sm" data-testid="kpi-table">
                        <CardHeader className="border-b border-slate-200 py-3">
                            <CardTitle className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                <Target className="h-4 w-4 text-brand" /> Level 1–4 RM KPI Framework
                            </CardTitle>
                            <p className="text-[11px] text-slate-500 mt-1">Activity → Pipeline → Output → Revenue</p>
                        </CardHeader>
                        <CardContent className="p-0 overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="border-b border-slate-200 bg-slate-50">
                                        <th className="text-left p-2 font-bold uppercase text-slate-500">RM</th>
                                        <th className="text-center p-2 font-bold uppercase text-slate-500" title="Level 1 Activity">Logins</th>
                                        <th className="text-center p-2 font-bold uppercase text-slate-500">Att. Days</th>
                                        <th className="text-center p-2 font-bold uppercase text-slate-500">Visits</th>
                                        <th className="text-center p-2 font-bold uppercase text-slate-500" title="Level 2 Pipeline">Leads</th>
                                        <th className="text-center p-2 font-bold uppercase text-slate-500">App Dwn</th>
                                        <th className="text-center p-2 font-bold uppercase text-slate-500" title="Level 3 Output">Loan App.</th>
                                        <th className="text-center p-2 font-bold uppercase text-slate-500">Disb.</th>
                                        <th className="text-center p-2 font-bold uppercase text-slate-500">Conv. %</th>
                                        <th className="text-right p-2 font-bold uppercase text-slate-500" title="Level 4 Revenue">Net Rev.</th>
                                        <th className="text-center p-2 font-bold uppercase text-slate-500">Salary Just %</th>
                                        <th className="text-center p-2 font-bold uppercase text-slate-500">Category</th>
                                        <th className="text-right p-2 font-bold uppercase text-slate-500">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {kpiData.kpis.map(k => (
                                        <tr key={k.rm_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`kpi-row-${k.rm_id}`}>
                                            <td className="p-2"><p className="font-medium text-slate-900">{k.rm_name}</p><p className="text-[10px] text-slate-400">{k.employee_id}</p></td>
                                            <td className="p-2 text-center"><span className={k.login_count < (kpiData.login_threshold || 40) ? 'text-red-600 font-bold' : 'text-slate-700'}>{k.login_count ?? 0}</span></td>
                                            <td className="p-2 text-center">{k.attendance_days}</td>
                                            <td className="p-2 text-center">{k.field_visits}</td>
                                            <td className="p-2 text-center">{k.total_leads}</td>
                                            <td className="p-2 text-center">{k.funnel.app_downloaded || 0}</td>
                                            <td className="p-2 text-center">{k.funnel.loan_applied || 0}</td>
                                            <td className="p-2 text-center font-bold text-slate-900">{k.funnel.disbursed || 0}</td>
                                            <td className="p-2 text-center"><span className={k.conversion_pct < 8 ? 'text-red-600 font-bold' : 'text-slate-700'}>{k.conversion_pct}%</span></td>
                                            <td className="p-2 text-right font-bold text-green-700">Rs.{k.net_revenue.toLocaleString()}</td>
                                            <td className="p-2 text-center"><Badge className={`rounded-none text-[10px] ${k.justification_pct >= 200 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{k.justification_pct}%</Badge></td>
                                            <td className="p-2 text-center">
                                                <Badge className={`rounded-none text-[10px] ${perfBadge(k.performance_category)}`}>{k.performance_category}</Badge>
                                                {k.last_coaching && (
                                                    <div className="mt-1 inline-flex items-center gap-1 text-[9px] text-emerald-700 font-semibold" title={`${k.last_coaching.type.replace('_',' ')} by ${k.last_coaching.by}`}>
                                                        <ShieldCheck className="h-3 w-3" />Coached {formatCoachedDate(k.last_coaching.date)}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="p-2 text-right">
                                                <div className="flex justify-end gap-1">
                                                    <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" onClick={() => openTimeline(k)} data-testid={`timeline-${k.rm_id}`}><Activity className="h-3 w-3 mr-1" />Timeline</Button>
                                                    <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" onClick={() => openDailyReport(k)} data-testid={`daily-report-${k.rm_id}`}><FileText className="h-3 w-3 mr-1" />Report</Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    {kpiData.kpis.length === 0 && <tr><td colSpan="13" className="p-8 text-center text-slate-500">No RM data</td></tr>}
                                </tbody>
                            </table>
                        </CardContent>
                    </Card>

                    {/* Vertical-wise breakdown for top RMs */}
                    <Card className="rounded-none border-slate-200" data-testid="vertical-breakdown">
                        <CardHeader className="border-b border-slate-200 py-3">
                            <CardTitle className="text-sm font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>Vertical-wise KPIs (Teacher Mitra • Shopkeeper • CA/Builder)</CardTitle>
                        </CardHeader>
                        <CardContent className="p-4 space-y-4">
                            {kpiData.kpis.slice(0, 8).map(k => (
                                k.verticals.length > 0 && (
                                    <div key={`v-${k.rm_id}`} className="border border-slate-200 p-3">
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-sm font-bold text-slate-900">{k.rm_name}</p>
                                            <Badge className={`rounded-none text-[10px] ${perfBadge(k.performance_category)}`}>{k.performance_category}</Badge>
                                        </div>
                                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                            {k.verticals.map((v, idx) => (
                                                <div key={`${k.rm_id}-v-${idx}`} className="bg-slate-50 p-2 border border-slate-100">
                                                    <p className="text-[10px] uppercase text-slate-500 font-bold truncate">{v.category}</p>
                                                    <p className="text-sm font-bold text-slate-900 mt-1">{v.disbursed}/{v.leads} leads</p>
                                                    <p className="text-[11px] text-green-700 font-medium">Rs.{v.net_revenue.toLocaleString()}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )
                            ))}
                            {kpiData.kpis.every(k => k.verticals.length === 0) && <div className="text-center text-slate-400 py-4 text-sm">No vertical breakdown data yet</div>}
                        </CardContent>
                    </Card>
                </>
            )}

            {tab === 'leaderboard' && leaderboard && (
                <>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                            <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>RM Leaderboard</h3>
                            <p className="text-[11px] text-slate-500">Ambient peer pressure that converts dashboards into habits</p>
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" className="rounded-none" onClick={() => exportLeaderboardImage('download')} disabled={exporting} data-testid="download-leaderboard-btn">
                                <Download className="h-4 w-4 mr-2" />{exporting ? 'Generating...' : 'Download PNG'}
                            </Button>
                            <Button className="rounded-none bg-emerald-600 hover:bg-emerald-700" onClick={() => exportLeaderboardImage('whatsapp')} disabled={exporting} data-testid="share-whatsapp-btn">
                                <Share2 className="h-4 w-4 mr-2" />Share to WhatsApp
                            </Button>
                        </div>
                    </div>

                    {/* Vertical filter pills */}
                    <div className="flex items-center flex-wrap gap-2 bg-white border border-slate-200 rounded-xl p-2 shadow-sm" data-testid="leaderboard-vertical-pills">
                        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 px-2">Vertical:</span>
                        {[
                            { key: 'all', label: 'All RMs' },
                            { key: 'Teacher Mitra', label: 'Teacher Mitra' },
                            { key: 'Shopkeeper', label: 'Shopkeeper' },
                            { key: 'CA-Builder', label: 'CA / Builder' },
                        ].map(v => (
                            <button
                                key={v.key}
                                onClick={() => { setLeaderboardVertical(v.key); fetchLeaderboardByVertical(v.key); }}
                                disabled={leaderboardLoading}
                                className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${leaderboardVertical === v.key ? 'bg-[#2563eb] text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'}`}
                                data-testid={`leaderboard-vertical-${v.key.replace(/\s+/g, '-').toLowerCase()}`}
                            >
                                {v.label}
                            </button>
                        ))}
                        {leaderboardLoading && <span className="text-[11px] text-slate-400 px-2">Loading...</span>}
                        {leaderboard.vertical && <span className="text-[11px] text-slate-500 ml-auto px-2">Filtered: <strong>{leaderboard.vertical}</strong></span>}
                    </div>

                    <div ref={leaderboardRef} className="bg-white p-6 border border-slate-200" data-testid="leaderboard-shareable">
                        {/* Branded Header — only visible inside the shareable image */}
                        <div className="flex items-center justify-between border-b-2 border-brand pb-3 mb-4">
                            <div>
                                <p className="text-2xl font-black text-brand tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>DIGI-LOANS</p>
                                <p className="text-[11px] uppercase tracking-widest text-slate-500 font-bold">RM Leaderboard • {MONTHS[m - 1]} {y}</p>
                            </div>
                            <div className="text-right">
                                <Trophy className="h-10 w-10 text-amber-500 inline-block" />
                                <p className="text-[10px] text-slate-400 mt-1">Generated {new Date().toLocaleDateString()}</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {/* Top Revenue */}
                            <Card className="rounded-none border-slate-200 border-t-4 border-t-amber-500" data-testid="leaderboard-revenue">
                            <CardHeader className="border-b border-slate-200 py-3 bg-gradient-to-r from-amber-50 to-white">
                                <CardTitle className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                    <Crown className="h-4 w-4 text-amber-600" /> Top Revenue — {MONTHS[m - 1]} {y}
                                </CardTitle>
                                <p className="text-[11px] text-slate-500 mt-1">Net revenue this month</p>
                            </CardHeader>
                            <CardContent className="p-0 divide-y divide-slate-100">
                                {leaderboard.top_revenue.length === 0 && <div className="p-6 text-center text-slate-400 text-sm">No revenue yet this month</div>}
                                {leaderboard.top_revenue.map((rm, i) => {
                                    const rankColor = i === 0 ? 'bg-amber-500 text-white' : i === 1 ? 'bg-slate-300 text-slate-800' : i === 2 ? 'bg-orange-300 text-orange-900' : 'bg-slate-100 text-slate-600';
                                    return (
                                        <div key={`rev-${rm.rm_id}`} className="flex items-center gap-3 p-3 hover:bg-slate-50" data-testid={`leaderboard-revenue-${i}`}>
                                            <div className={`flex-shrink-0 h-9 w-9 ${rankColor} flex items-center justify-center font-black text-sm`} style={{ fontFamily: 'Chivo, sans-serif' }}>
                                                {i + 1}
                                            </div>
                                            <div className="flex-shrink-0 h-10 w-10 bg-brand text-white flex items-center justify-center font-bold text-xs" title={rm.name}>
                                                {rm.initials}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-bold text-slate-900 truncate text-sm">{rm.name}</p>
                                                <div className="flex items-center gap-2 text-[10px] text-slate-500">
                                                    <span>{rm.deals} deals</span>
                                                    <span>•</span>
                                                    <span>{rm.justification_pct}% just.</span>
                                                    {rm.streak_days > 0 && <><span>•</span><span className="inline-flex items-center text-amber-600 font-bold"><Flame className="h-3 w-3" />{rm.streak_days}d</span></>}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-sm font-black text-emerald-700 tabular-nums" style={{ fontFamily: 'Chivo, sans-serif' }}>Rs.{rm.net_revenue.toLocaleString()}</p>
                                                <p className="text-[10px] text-slate-400">net rev.</p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </CardContent>
                        </Card>

                        {/* Top Streaks */}
                        <Card className="rounded-none border-slate-200 border-t-4 border-t-orange-500" data-testid="leaderboard-streaks">
                            <CardHeader className="border-b border-slate-200 py-3 bg-gradient-to-r from-orange-50 to-white">
                                <CardTitle className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                    <Flame className="h-4 w-4 text-orange-600" /> Hottest Streaks — Live
                                </CardTitle>
                                <p className="text-[11px] text-slate-500 mt-1">Consecutive working days hitting daily ask</p>
                            </CardHeader>
                            <CardContent className="p-0 divide-y divide-slate-100">
                                {leaderboard.top_streaks.length === 0 && <div className="p-6 text-center text-slate-400 text-sm">No active streaks</div>}
                                {leaderboard.top_streaks.map((rm, i) => {
                                    const rankColor = i === 0 ? 'bg-amber-500 text-white' : i === 1 ? 'bg-slate-300 text-slate-800' : i === 2 ? 'bg-orange-300 text-orange-900' : 'bg-slate-100 text-slate-600';
                                    const flameSize = rm.streak_days >= 21 ? 'text-orange-600' : rm.streak_days >= 7 ? 'text-orange-500' : rm.streak_days >= 3 ? 'text-amber-500' : 'text-slate-300';
                                    return (
                                        <div key={`st-${rm.rm_id}`} className="flex items-center gap-3 p-3 hover:bg-slate-50" data-testid={`leaderboard-streak-${i}`}>
                                            <div className={`flex-shrink-0 h-9 w-9 ${rankColor} flex items-center justify-center font-black text-sm`} style={{ fontFamily: 'Chivo, sans-serif' }}>
                                                {i + 1}
                                            </div>
                                            <div className="flex-shrink-0 h-10 w-10 bg-brand text-white flex items-center justify-center font-bold text-xs" title={rm.name}>
                                                {rm.initials}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-bold text-slate-900 truncate text-sm">{rm.name}</p>
                                                <p className="text-[10px] text-slate-500 truncate">Rs.{rm.net_revenue.toLocaleString()} net • {rm.deals} deals</p>
                                            </div>
                                            <div className="text-right flex items-center gap-1">
                                                <Flame className={`h-6 w-6 ${flameSize} ${rm.streak_days >= 3 ? 'fill-current opacity-30' : ''}`} />
                                                <div>
                                                    <p className="text-xl font-black text-slate-900 tabular-nums leading-none" style={{ fontFamily: 'Chivo, sans-serif' }}>{rm.streak_days}</p>
                                                    <p className="text-[9px] uppercase text-slate-400 font-bold">days</p>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </CardContent>
                        </Card>
                        </div>

                        {/* Footer — only visible in shareable image */}
                        <div className="border-t border-slate-200 mt-4 pt-3 flex items-center justify-between">
                            <p className="text-[10px] text-slate-400">Powered by DIGI-LOANS HRM • Streak rule: ≥16 leads + login, or 1 disbursement, or 1 visit per day</p>
                            <p className="text-[10px] text-slate-400 font-bold">www.digi-loan.com</p>
                        </div>
                    </div>
                </>
            )}

            {tab === 'alerts' && kpiData && (
                <>
                    <Card className="rounded-none border-slate-200 border-l-4 border-l-red-500" data-testid="alerts-panel">
                        <CardHeader className="border-b border-slate-200 py-3">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                                <div>
                                    <CardTitle className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                        <AlertTriangle className="h-4 w-4 text-red-600" /> Low Performer Alerts — {MONTHS[m - 1]} {y}
                                    </CardTitle>
                                    <p className="text-[11px] text-slate-500 mt-1">Flagged: Revenue &lt; 200% of salary • Conversion &lt; 8% • Logins &lt; 40/m</p>
                                </div>
                                <div className="flex border border-slate-200 w-fit" data-testid="alerts-filter">
                                    {[
                                        { key: 'uncoached', label: 'Un-coached', count: kpiData.kpis.filter(k => k.is_low_performer && !k.last_coaching).length },
                                        { key: 'coached', label: 'Coached', count: kpiData.kpis.filter(k => k.is_low_performer && k.last_coaching).length },
                                        { key: 'all', label: 'All', count: kpiData.kpis.filter(k => k.is_low_performer).length },
                                    ].map(f => (
                                        <Button key={f.key} size="sm" variant={alertsFilter === f.key ? 'default' : 'ghost'} className="rounded-none h-8 text-[10px]" onClick={() => setAlertsFilter(f.key)} data-testid={`alerts-filter-${f.key}`}>
                                            {f.label} <span className="ml-1 opacity-70">({f.count})</span>
                                        </Button>
                                    ))}
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0">
                            {(() => {
                                const flagged = kpiData.kpis.filter(k => k.is_low_performer);
                                const filtered = flagged.filter(k => {
                                    if (alertsFilter === 'coached') return !!k.last_coaching;
                                    if (alertsFilter === 'uncoached') return !k.last_coaching;
                                    return true;
                                });
                                if (filtered.length === 0) {
                                    return (
                                        <div className="p-8 text-center text-slate-500 flex flex-col items-center gap-2">
                                            <CheckCircle2 className="h-8 w-8 text-green-600" />
                                            <p>{alertsFilter === 'uncoached' ? 'All flagged RMs have been coached this month' : flagged.length === 0 ? 'No low performers this month' : 'No RMs match this filter'}</p>
                                        </div>
                                    );
                                }
                                return (
                                    <div className="divide-y divide-slate-200">
                                        {filtered.map(k => (
                                    <div key={`alert-${k.rm_id}`} className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-testid={`alert-${k.rm_id}`}>
                                        <div>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <p className="font-bold text-slate-900">{k.rm_name}</p>
                                                <Badge className="rounded-none bg-red-100 text-red-700 text-[10px]">{k.employee_id || '-'}</Badge>
                                                {k.active_catchup && (
                                                    <Badge className={`rounded-none text-[10px] font-bold ${k.active_catchup.is_overdue ? 'bg-red-600 text-white animate-pulse' : 'bg-amber-100 text-amber-800'}`} data-testid={`catchup-chip-${k.rm_id}`}>
                                                        ⏱ Catch-up: {k.active_catchup.is_overdue ? 'OVERDUE' : `${k.active_catchup.days_left}d left`}
                                                    </Badge>
                                                )}
                                                {k.last_coaching && !k.active_catchup && (
                                                    <Badge className="rounded-none bg-emerald-100 text-emerald-700 text-[10px] font-bold" data-testid={`coached-badge-${k.rm_id}`}>
                                                        <ShieldCheck className="h-3 w-3 mr-1" />Coached {formatCoachedDate(k.last_coaching.date)}
                                                    </Badge>
                                                )}
                                            </div>
                                            <div className="flex flex-wrap gap-2 mt-2">
                                                {k.alert_flags.map((f) => (
                                                    <span key={`${k.rm_id}-${f}`} className="text-[10px] font-medium px-2 py-0.5 bg-red-50 text-red-700 border border-red-200">{f}</span>
                                                ))}
                                            </div>
                                            <p className="text-[11px] text-slate-500 mt-2">
                                                Net Rev: <span className="font-bold text-green-700">Rs.{k.net_revenue.toLocaleString()}</span> • Conv: <span className="font-bold">{k.conversion_pct}%</span> • Just: <span className="font-bold">{k.justification_pct}%</span>
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-bold uppercase text-slate-400 mb-1">Recommended Action</p>
                                            <ul className="text-[11px] text-slate-600 space-y-0.5">
                                                <li>• Weekly 1-on-1 review</li>
                                                <li>• Field monitoring + ride-along</li>
                                                <li>• Refresher training</li>
                                            </ul>
                                            <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px] mt-2" onClick={() => openTimeline(k, 14)} data-testid={`alert-timeline-${k.rm_id}`}><Activity className="h-3 w-3 mr-1" />View 14d Timeline</Button>
                                        </div>
                                    </div>
                                ))}
                                    </div>
                                );
                            })()}
                        </CardContent>
                    </Card>
                </>
            )}

            {tab === 'justification' && justification && (
                <>
                    <SalaryBenchmarkCard ladder={justification?.slabs} />
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="sm" className="rounded-none" onClick={() => setCurrentDate(new Date(y, m - 2, 1))}><ChevronLeft className="h-4 w-4" /></Button>
                        <span className="text-sm font-bold text-slate-700">{MONTHS[m - 1]} {y}</span>
                        <Button variant="ghost" size="sm" className="rounded-none" onClick={() => setCurrentDate(new Date(y, m, 1))}><ChevronRight className="h-4 w-4" /></Button>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                        <Card className="rounded-none border-slate-200"><CardContent className="p-5"><p className="text-[10px] font-bold uppercase text-slate-400">Gross Revenue</p><p className="text-2xl font-black text-brand mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>Rs.{(justification.totals?.gross_revenue || 0).toLocaleString()}</p></CardContent></Card>
                        <Card className="rounded-none border-slate-200"><CardContent className="p-5"><p className="text-[10px] font-bold uppercase text-slate-400">Total Expense</p><p className="text-2xl font-black text-red-600 mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>Rs.{(justification.totals?.total_expense || 0).toLocaleString()}</p></CardContent></Card>
                        <Card className="rounded-none border-slate-200"><CardContent className="p-5"><p className="text-[10px] font-bold uppercase text-slate-400">Net Revenue</p><p className="text-2xl font-black text-green-700 mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>Rs.{(justification.totals?.net_revenue || 0).toLocaleString()}</p></CardContent></Card>
                    </div>

                    <Card className="rounded-none border-slate-200" data-testid="justification-table"><CardContent className="p-0 overflow-x-auto">
                        <table className="w-full"><thead><tr className="border-b border-slate-200 bg-slate-50">
                            <th className="text-left p-3 text-xs font-bold uppercase text-slate-500">Employee</th>
                            <th className="text-right p-3 text-xs font-bold uppercase text-slate-500">Salary</th>
                            <th className="text-right p-3 text-xs font-bold uppercase text-slate-500">Expenses</th>
                            <th className="text-right p-3 text-xs font-bold uppercase text-slate-500">Cust. Reward</th>
                            <th className="text-right p-3 text-xs font-bold uppercase text-slate-500">Total Exp.</th>
                            <th className="text-right p-3 text-xs font-bold uppercase text-slate-500">Gross Rev.</th>
                            <th className="text-right p-3 text-xs font-bold uppercase text-slate-500">Net Rev.</th>
                            <th className="text-center p-3 text-xs font-bold uppercase text-slate-500">Justification</th>
                            <th className="text-center p-3 text-xs font-bold uppercase text-slate-500">Incentive</th>
                        </tr></thead><tbody>
                            {justification.justifications?.map(j => (
                                <tr key={j.rm_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`justification-${j.rm_id}`}>
                                    <td className="p-3"><p className="font-medium text-slate-900">{j.rm_name}</p><p className="text-[10px] text-slate-400">{j.leads_disbursed} leads</p></td>
                                    <td className="p-3 text-right text-sm">Rs.{j.salary.toLocaleString()}</td>
                                    <td className="p-3 text-right text-sm">Rs.{j.expenses.toLocaleString()}</td>
                                    <td className="p-3 text-right text-sm">Rs.{j.customer_rewards.toLocaleString()}</td>
                                    <td className="p-3 text-right text-sm font-bold text-red-600">Rs.{j.total_expense.toLocaleString()}</td>
                                    <td className="p-3 text-right text-sm">Rs.{j.gross_revenue.toLocaleString()}</td>
                                    <td className="p-3 text-right text-sm font-bold text-green-700">Rs.{j.net_revenue.toLocaleString()}</td>
                                    <td className="p-3 text-center"><Badge className={`rounded-none text-xs ${j.justification_pct >= 200 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{j.justification_pct}%</Badge></td>
                                    <td className="p-3 text-center">{j.eligible_incentive ? <span className="font-bold text-green-700">Rs.{j.incentive_amount.toLocaleString()} ({j.incentive_pct}%)</span> : <span className="text-slate-400">-</span>}</td>
                                </tr>
                            ))}
                            {(!justification.justifications || justification.justifications.length === 0) && <tr><td colSpan="9" className="p-8 text-center text-slate-500">No employee data</td></tr>}
                        </tbody></table>
                    </CardContent></Card>
                </>
            )}

            {tab === 'incentives' && (
                <>
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Incentive Slab Structure</h2>
                        <div className="flex gap-2">
                            <Button variant="outline" className="rounded-none" onClick={resetSlabsToDefault} data-testid="reset-default-slabs-btn">
                                <Award className="h-4 w-4 mr-2" />Reset to Default Ladder
                            </Button>
                            <Dialog open={slabDialog} onOpenChange={setSlabDialog}>
                            <DialogTrigger asChild><Button className="bg-brand hover:bg-brand rounded-none" data-testid="add-slab-btn"><Plus className="h-4 w-4 mr-2" />Add Slab</Button></DialogTrigger>
                            <DialogContent className="rounded-none max-w-md">
                                <DialogHeader><DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Add Incentive Slab</DialogTitle><DialogDescription>Define a revenue range and its matching incentive percentage.</DialogDescription></DialogHeader>
                                <form onSubmit={createSlab} className="space-y-4 mt-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div><Label className="text-xs font-bold uppercase text-slate-500">Min % (Revenue/Salary)</Label>
                                            <Input type="number" value={slabForm.min_percentage} onChange={(e) => setSlabForm({...slabForm, min_percentage: e.target.value})} className="rounded-none mt-1" required /></div>
                                        <div><Label className="text-xs font-bold uppercase text-slate-500">Max % (0 = unlimited)</Label>
                                            <Input type="number" value={slabForm.max_percentage} onChange={(e) => setSlabForm({...slabForm, max_percentage: e.target.value})} className="rounded-none mt-1" /></div>
                                    </div>
                                    <div><Label className="text-xs font-bold uppercase text-slate-500">Incentive % (of Salary)</Label>
                                        <Input type="number" step="0.5" value={slabForm.incentive_percentage} onChange={(e) => setSlabForm({...slabForm, incentive_percentage: e.target.value})} className="rounded-none mt-1" required /></div>
                                    <div className="flex justify-end gap-3"><Button type="button" variant="outline" className="rounded-none" onClick={() => setSlabDialog(false)}>Cancel</Button>
                                        <Button type="submit" className="bg-brand hover:bg-brand rounded-none" data-testid="save-slab-btn">Create Slab</Button></div>
                                </form>
                            </DialogContent>
                        </Dialog>
                        </div>
                    </div>
                    <Card className="rounded-none border-slate-200"><CardContent className="p-0">
                        <table className="w-full"><thead><tr className="border-b border-slate-200 bg-slate-50">
                            <th className="text-left p-3 text-xs font-bold uppercase text-slate-500">Revenue/Salary Range</th>
                            <th className="text-center p-3 text-xs font-bold uppercase text-slate-500">Incentive %</th>
                            <th className="text-right p-3 text-xs font-bold uppercase text-slate-500">Action</th>
                        </tr></thead><tbody>
                            {justification?.slabs?.map(slab => (
                                <tr key={slab._id} className="border-b border-slate-100">
                                    <td className="p-3 font-medium text-slate-900">{slab.min_percentage}% — {slab.max_percentage > 0 ? `${slab.max_percentage}%` : 'Unlimited'}</td>
                                    <td className="p-3 text-center"><Badge className="rounded-none bg-green-100 text-green-700 font-bold">{slab.incentive_percentage}%</Badge></td>
                                    <td className="p-3 text-right"><Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-600 rounded-none" onClick={() => deleteSlab(slab._id)}><Trash2 className="h-3.5 w-3.5" /></Button></td>
                                </tr>
                            ))}
                            {(!justification?.slabs || justification.slabs.length === 0) && <tr><td colSpan="3" className="p-8 text-center text-slate-500">No slabs defined. Revenue &gt; 200% of salary = eligible for incentive.</td></tr>}
                        </tbody></table>
                    </CardContent></Card>
                </>
            )}

            {tab === 'auto-share' && shareSettings && (
                <>
                <Card className="rounded-xl shadow-sm border-slate-200 overflow-hidden" data-testid="crm-pacing-panel">
                    <CardHeader className="border-b border-slate-200 py-3 bg-slate-50/50">
                        <CardTitle className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            <Calendar className="h-4 w-4 text-[#2563eb]" /> CRM Pacing
                        </CardTitle>
                        <p className="text-[11px] text-slate-500 mt-1">Working days per month feeds the "Today's Plan" pacing math, daily-target leads, and streak rules.</p>
                    </CardHeader>
                    <CardContent className="p-4">
                        <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                            <div className="sm:max-w-[220px]">
                                <Label className="text-xs font-bold uppercase text-slate-500">Working days / month</Label>
                                <Input
                                    type="number"
                                    min="18"
                                    max="31"
                                    value={crmConfig.working_days_per_month}
                                    onChange={(e) => setCrmConfig({ working_days_per_month: e.target.value })}
                                    className="rounded-lg mt-1 bg-slate-50 border-slate-200 tabular-nums"
                                    data-testid="working-days-input"
                                />
                                <p className="text-[10px] text-slate-400 mt-1">Default 25 · Range 18–31</p>
                            </div>
                            <Button
                                onClick={saveCrmConfig}
                                disabled={crmConfigSaving}
                                className="rounded-lg bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-sm"
                                data-testid="save-working-days-btn"
                            >
                                {crmConfigSaving ? 'Saving...' : 'Save Pacing'}
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                <Card className="rounded-xl shadow-sm border-slate-200 overflow-hidden" data-testid="auto-share-panel">
                    <CardHeader className="border-b border-slate-200 py-3">
                        <CardTitle className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                            <Share2 className="h-4 w-4 text-brand" /> Auto-Share Schedule
                        </CardTitle>
                        <p className="text-[11px] text-slate-500 mt-1">Automatically email the RM Leaderboard digest on a recurring schedule. WhatsApp Business API is wired but disabled until WABA credentials are added.</p>
                    </CardHeader>
                    <CardContent className="p-4 space-y-5">
                        <div className="flex items-center gap-3">
                            <input type="checkbox" id="share-enabled" checked={!!shareSettings.enabled} onChange={(e) => updateShareField('enabled', e.target.checked)} className="h-4 w-4 accent-brand" data-testid="share-enabled" />
                            <label htmlFor="share-enabled" className="text-sm font-bold text-slate-900 cursor-pointer">Enable automatic delivery</label>
                            {shareSettings.next_run_at && shareSettings.enabled && (
                                <span className="ml-auto text-[11px] text-emerald-700 font-bold bg-emerald-50 px-2 py-1 border border-emerald-200" data-testid="next-run-chip">
                                    Next run: {new Date(shareSettings.next_run_at).toLocaleString()}
                                </span>
                            )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                                <Label className="text-xs font-bold uppercase text-slate-500">Day</Label>
                                <select value={shareSettings.day_of_week} onChange={(e) => updateShareField('day_of_week', parseInt(e.target.value))} className="w-full rounded-none border border-slate-200 px-3 py-2 text-sm mt-1" data-testid="share-day">
                                    {['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map((d, i) => <option key={d} value={i}>{d}</option>)}
                                </select>
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase text-slate-500">Hour (IST, 24h)</Label>
                                <Input type="number" min="0" max="23" value={shareSettings.hour} onChange={(e) => updateShareField('hour', parseInt(e.target.value) || 0)} className="rounded-none mt-1" data-testid="share-hour" />
                            </div>
                            <div>
                                <Label className="text-xs font-bold uppercase text-slate-500">Minute</Label>
                                <Input type="number" min="0" max="59" value={shareSettings.minute} onChange={(e) => updateShareField('minute', parseInt(e.target.value) || 0)} className="rounded-none mt-1" data-testid="share-minute" />
                            </div>
                        </div>

                        <div>
                            <Label className="text-xs font-bold uppercase text-slate-500">Recipients (one email per line)</Label>
                            <Textarea
                                value={(shareSettings.recipients || []).join('\n')}
                                onChange={(e) => updateShareField('recipients', e.target.value.split('\n').map(s => s.trim()).filter(Boolean))}
                                className="rounded-none mt-1 font-mono text-xs"
                                rows={4}
                                placeholder="ceo@yourcompany.com&#10;hr@yourcompany.com&#10;ops@yourcompany.com"
                                data-testid="share-recipients"
                            />
                            <p className="text-[10px] text-slate-400 mt-1">{(shareSettings.recipients || []).length} recipient(s) configured</p>
                        </div>

                        <div className="border border-slate-200 bg-slate-50 p-3">
                            <div className="flex items-center justify-between">
                                <Label className="text-xs font-bold uppercase text-slate-500 flex items-center gap-2">
                                    WhatsApp Business API <span className="text-[10px] font-normal text-slate-400">(coming soon)</span>
                                </Label>
                                <input type="checkbox" checked={!!shareSettings.whatsapp_enabled} onChange={(e) => updateShareField('whatsapp_enabled', e.target.checked)} className="h-4 w-4 accent-emerald-600" disabled data-testid="whatsapp-enabled" />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2 opacity-60">
                                <div>
                                    <Label className="text-[10px] uppercase text-slate-500">Phone Number ID</Label>
                                    <Input value={shareSettings.whatsapp_phone_id || ''} onChange={(e) => updateShareField('whatsapp_phone_id', e.target.value)} className="rounded-none mt-1 text-xs" disabled />
                                </div>
                                <div>
                                    <Label className="text-[10px] uppercase text-slate-500">API Key</Label>
                                    <Input type="password" value={shareSettings.whatsapp_api_key || ''} onChange={(e) => updateShareField('whatsapp_api_key', e.target.value)} placeholder={shareSettings.whatsapp_api_key_masked || 'EAA...'} className="rounded-none mt-1 text-xs" disabled />
                                </div>
                            </div>
                            <p className="text-[10px] text-slate-400 mt-2">Requires Meta Business Manager + WABA approval (1–3 weeks). Email digest works today.</p>
                        </div>

                        {shareSettings.last_sent_at && (
                            <div className="text-[11px] text-slate-500 border-t border-slate-100 pt-3">
                                Last digest sent: <span className="font-mono">{new Date(shareSettings.last_sent_at).toLocaleString()}</span> · {shareSettings.last_send_status || ''}
                            </div>
                        )}

                        <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
                            <Button className="rounded-lg bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-sm" onClick={saveShareSettings} disabled={shareSaving} data-testid="save-share-btn">
                                {shareSaving ? 'Saving...' : 'Save Schedule'}
                            </Button>
                            <Button variant="outline" className="rounded-lg" onClick={sendDigestToMe} disabled={shareDigestSending} data-testid="send-to-me-btn">
                                <FileText className="h-4 w-4 mr-2" />{shareDigestSending ? 'Sending...' : 'Send to me (preview)'}
                            </Button>
                            <Button variant="outline" className="rounded-lg" onClick={sendDigestNow} disabled={shareDigestSending || !shareSettings.recipients?.length} data-testid="send-now-btn">
                                <Share2 className="h-4 w-4 mr-2" />{shareDigestSending ? 'Sending...' : 'Send digest now (broadcast)'}
                            </Button>
                            <Button variant="ghost" className="rounded-lg" onClick={fetchPreview} disabled={previewLoading} data-testid="refresh-preview-btn">
                                <FileText className="h-4 w-4 mr-2" />{previewLoading ? 'Refreshing...' : 'Refresh preview'}
                            </Button>
                        </div>

                        {/* Email Preview Pane */}
                        <div className="border border-slate-200 mt-4" data-testid="email-preview-pane">
                            <div className="bg-slate-100 px-3 py-2 border-b border-slate-200 flex items-center justify-between">
                                <div>
                                    <p className="text-[10px] font-bold uppercase text-slate-500">Email Preview</p>
                                    <p className="text-xs text-slate-700 font-bold mt-0.5" data-testid="preview-subject">{previewSubject || '—'}</p>
                                </div>
                                <span className="text-[10px] text-slate-500">From: onboarding@resend.dev (or your verified domain)</span>
                            </div>
                            {previewLoading && <div className="h-72 flex items-center justify-center text-slate-400 text-sm">Generating preview...</div>}
                            {!previewLoading && previewHtml && (
                                <iframe
                                    title="Email digest preview"
                                    srcDoc={previewHtml}
                                    className="w-full h-[560px] border-0 bg-white"
                                    sandbox=""
                                    data-testid="preview-iframe"
                                />
                            )}
                            {!previewLoading && !previewHtml && <div className="h-72 flex items-center justify-center text-slate-400 text-sm">Click "Refresh preview" to generate</div>}
                        </div>
                    </CardContent>
                </Card>
                </>
            )}

            {tab === 'catchup-audit' && canManage && (
                <div className="space-y-4" data-testid="catchup-audit-panel">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                            <h3 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Catch-up Audit Trail</h3>
                            <p className="text-[11px] text-slate-500">Forensic view of every recovery period — who triggered, deadline, outcome, linked coaching.</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <select
                                value={catchupAuditDays}
                                onChange={(e) => setCatchupAuditDays(parseInt(e.target.value))}
                                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs bg-white shadow-sm"
                                data-testid="catchup-audit-window"
                            >
                                <option value={30}>Last 30 days</option>
                                <option value={90}>Last 90 days</option>
                                <option value={180}>Last 180 days</option>
                                <option value={365}>Last 12 months</option>
                            </select>
                            <select
                                value={catchupAuditStatus}
                                onChange={(e) => setCatchupAuditStatus(e.target.value)}
                                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs bg-white shadow-sm"
                                data-testid="catchup-audit-status"
                            >
                                <option value="all">All statuses</option>
                                <option value="active">Active</option>
                                <option value="recovered">Recovered</option>
                                <option value="escalated">Escalated</option>
                                <option value="cancelled">Cancelled</option>
                                <option value="superseded">Superseded</option>
                            </select>
                            <Button variant="outline" className="rounded-lg" onClick={fetchCatchupAudit} disabled={catchupAuditLoading} data-testid="catchup-audit-refresh">
                                {catchupAuditLoading ? 'Loading...' : 'Refresh'}
                            </Button>
                        </div>
                    </div>

                    {catchupAudit && (
                        <>
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                <Card className="bg-white border border-slate-200 rounded-xl shadow-sm border-t-4 border-t-[#2563eb] overflow-hidden">
                                    <CardContent className="p-5">
                                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Total Catch-ups</p>
                                        <p className="text-2xl font-extrabold text-slate-900 mt-2 tabular-nums" style={{ fontFamily: 'Chivo, sans-serif' }}>{catchupAudit.total}</p>
                                        <p className="text-xs text-slate-500 mt-1">Last {catchupAudit.window_days} days</p>
                                    </CardContent>
                                </Card>
                                <Card className="bg-white border border-slate-200 rounded-xl shadow-sm border-t-4 border-t-emerald-500 overflow-hidden">
                                    <CardContent className="p-5">
                                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Recovery Rate</p>
                                        <p className="text-2xl font-extrabold text-emerald-600 mt-2 tabular-nums" style={{ fontFamily: 'Chivo, sans-serif' }}>{catchupAudit.recovery_rate_pct}%</p>
                                        <p className="text-xs text-slate-500 mt-1">{catchupAudit.by_status?.recovered || 0} recovered</p>
                                    </CardContent>
                                </Card>
                                <Card className="bg-white border border-slate-200 rounded-xl shadow-sm border-t-4 border-t-amber-500 overflow-hidden">
                                    <CardContent className="p-5">
                                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Active Now</p>
                                        <p className="text-2xl font-extrabold text-amber-600 mt-2 tabular-nums" style={{ fontFamily: 'Chivo, sans-serif' }}>{catchupAudit.by_status?.active || 0}</p>
                                        <p className="text-xs text-slate-500 mt-1">In recovery</p>
                                    </CardContent>
                                </Card>
                                <Card className="bg-white border border-slate-200 rounded-xl shadow-sm border-t-4 border-t-purple-500 overflow-hidden">
                                    <CardContent className="p-5">
                                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Avg Duration</p>
                                        <p className="text-2xl font-extrabold text-slate-900 mt-2 tabular-nums" style={{ fontFamily: 'Chivo, sans-serif' }}>{catchupAudit.avg_duration_days}d</p>
                                        <p className="text-xs text-slate-500 mt-1">Time-to-resolve</p>
                                    </CardContent>
                                </Card>
                            </div>

                            <Card className="rounded-xl shadow-sm border-slate-200 overflow-hidden">
                                <CardHeader className="border-b border-slate-200 bg-slate-50/50 py-3">
                                    <CardTitle className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                        <Flag className="h-4 w-4 text-[#2563eb]" /> Catch-up Records ({catchupAudit.rows.length})
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="p-0">
                                    <div className="overflow-x-auto">
                                        <table className="w-full" data-testid="catchup-audit-table">
                                            <thead>
                                                <tr className="border-b border-slate-200 bg-slate-50">
                                                    <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">RM</th>
                                                    <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Started By</th>
                                                    <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Started</th>
                                                    <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Deadline</th>
                                                    <th className="text-center p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Window</th>
                                                    <th className="text-center p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Duration</th>
                                                    <th className="text-center p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Outcome</th>
                                                    <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Coaching Linked</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {catchupAudit.rows.length === 0 && (
                                                    <tr><td colSpan={8} className="p-8 text-center text-slate-400 text-sm">No catch-up records in this window.</td></tr>
                                                )}
                                                {catchupAudit.rows.map(row => {
                                                    const statusColors = {
                                                        active: 'bg-amber-100 text-amber-700 border-amber-200',
                                                        recovered: 'bg-emerald-100 text-emerald-700 border-emerald-200',
                                                        escalated: 'bg-red-100 text-red-700 border-red-200',
                                                        cancelled: 'bg-slate-100 text-slate-600 border-slate-200',
                                                        superseded: 'bg-purple-100 text-purple-700 border-purple-200',
                                                    };
                                                    const fmt = (iso) => iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' }) : '—';
                                                    return (
                                                        <tr key={row._id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`catchup-row-${row._id}`}>
                                                            <td className="p-3"><p className="font-medium text-slate-900 text-sm">{row.rm_name}</p></td>
                                                            <td className="p-3 text-xs text-slate-600">{row.started_by_name}</td>
                                                            <td className="p-3 text-xs text-slate-600 tabular-nums">{fmt(row.started_at)}</td>
                                                            <td className="p-3 text-xs text-slate-600 tabular-nums">{fmt(row.deadline_at)}</td>
                                                            <td className="p-3 text-center text-xs text-slate-600">{row.days}d × {row.daily_multiplier}</td>
                                                            <td className="p-3 text-center text-xs text-slate-700 tabular-nums font-medium">
                                                                {row.duration_days != null ? `${row.duration_days}d` : '—'}
                                                                {row.beat_deadline === true && <span className="ml-1 text-emerald-600 text-[10px] font-bold">on-time</span>}
                                                                {row.beat_deadline === false && <span className="ml-1 text-red-600 text-[10px] font-bold">late</span>}
                                                            </td>
                                                            <td className="p-3 text-center">
                                                                <Badge className={`rounded-full px-2 py-0.5 text-[10px] capitalize ${statusColors[row.status] || 'bg-slate-100 text-slate-600'}`}>{row.status}</Badge>
                                                            </td>
                                                            <td className="p-3 text-xs text-slate-600">
                                                                {row.coaching ? (
                                                                    <div>
                                                                        <p className="font-medium capitalize">{(row.coaching.session_type || '').replace(/_/g, ' ')}</p>
                                                                        <p className="text-[10px] text-slate-400">by {row.coaching.reviewed_by_name}</p>
                                                                    </div>
                                                                ) : <span className="text-slate-300">—</span>}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </CardContent>
                            </Card>
                        </>
                    )}
                </div>
            )}
            <Dialog open={!!dailyReportDialog} onOpenChange={(o) => !o && setDailyReportDialog(null)}>
                <DialogContent className="rounded-none max-w-lg">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Daily Report — {dailyReportDialog?.rm_name}</DialogTitle>
                        <DialogDescription>WhatsApp-ready summary of today's activity, pipeline and revenue.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 mt-3">
                        {dailyReportLoading ? (
                            <div className="h-48 flex items-center justify-center text-slate-400">Generating report...</div>
                        ) : (
                            <>
                                <pre className="bg-slate-50 border border-slate-200 p-4 text-xs text-slate-700 whitespace-pre-wrap max-h-80 overflow-y-auto font-mono" data-testid="daily-report-text">{dailyReportText}</pre>
                                <div className="flex gap-2">
                                    <Button className="flex-1 rounded-none bg-brand hover:bg-brand" onClick={copyReport} data-testid="copy-report-btn">
                                        {copiedReport ? <><CheckCircle2 className="h-4 w-4 mr-2" />Copied!</> : <><Copy className="h-4 w-4 mr-2" />Copy</>}
                                    </Button>
                                    <Button variant="outline" className="flex-1 rounded-none" onClick={sendWhatsApp} data-testid="whatsapp-report-btn">Share on WhatsApp</Button>
                                </div>
                            </>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* RM Activity Timeline Dialog */}
            <Dialog open={!!timelineDialog} onOpenChange={(o) => !o && setTimelineDialog(null)}>
                <DialogContent className="rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
                            Activity Timeline — {timelineDialog?.rm_name}
                        </DialogTitle>
                        <DialogDescription>Chronological activity: logins, field visits, lead events and revenue bookings.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 mt-3">
                        <div className="flex items-center gap-2 border-b border-slate-200 pb-3">
                            <span className="text-xs text-slate-500">Range:</span>
                            {[7, 14, 30].map(d => (
                                <Button key={`d-${d}`} size="sm" variant={timelineDays === d ? 'default' : 'outline'} className="rounded-none h-7 text-[10px]" onClick={() => timelineDialog && openTimeline(timelineDialog, d)} data-testid={`timeline-days-${d}`}>
                                    {d}d
                                </Button>
                            ))}
                            <div className="ml-auto flex items-center gap-2">
                                {timelineDialog?.last_coaching && (
                                    <Badge className="rounded-none bg-emerald-100 text-emerald-700 text-[10px] font-bold">
                                        <ShieldCheck className="h-3 w-3 mr-1" />Coached {formatCoachedDate(timelineDialog.last_coaching.date)}
                                    </Badge>
                                )}
                                <Button size="sm" className="rounded-none h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700" onClick={() => openCoach(timelineDialog)} data-testid="mark-coached-btn">
                                    <GraduationCap className="h-3 w-3 mr-1" />Mark as Coached
                                </Button>
                            </div>
                        </div>
                        {timelineLoading && <div className="h-32 flex items-center justify-center text-slate-400 text-sm">Loading timeline...</div>}
                        {!timelineLoading && timelineData && (
                            <>
                                <div className="grid grid-cols-5 gap-2" data-testid="timeline-counts">
                                    <div className="bg-blue-50 border border-blue-100 p-2 text-center"><LogIn className="h-3 w-3 inline text-blue-600 mb-1" /><p className="text-xs font-bold text-slate-900">{timelineData.counts.login}</p><p className="text-[9px] uppercase text-slate-500">Logins</p></div>
                                    <div className="bg-indigo-50 border border-indigo-100 p-2 text-center"><MapPin className="h-3 w-3 inline text-indigo-600 mb-1" /><p className="text-xs font-bold text-slate-900">{timelineData.counts.visit}</p><p className="text-[9px] uppercase text-slate-500">Visits</p></div>
                                    <div className="bg-amber-50 border border-amber-100 p-2 text-center"><UserPlus className="h-3 w-3 inline text-amber-600 mb-1" /><p className="text-xs font-bold text-slate-900">{timelineData.counts.lead_created}</p><p className="text-[9px] uppercase text-slate-500">Leads</p></div>
                                    <div className="bg-purple-50 border border-purple-100 p-2 text-center"><Flag className="h-3 w-3 inline text-purple-600 mb-1" /><p className="text-xs font-bold text-slate-900">{timelineData.counts.lead_status}</p><p className="text-[9px] uppercase text-slate-500">Status</p></div>
                                    <div className="bg-green-50 border border-green-100 p-2 text-center"><IndianRupee className="h-3 w-3 inline text-green-700 mb-1" /><p className="text-xs font-bold text-slate-900">{timelineData.counts.revenue}</p><p className="text-[9px] uppercase text-slate-500">Revenue</p></div>
                                </div>
                                {timelineData.events.length === 0 ? (
                                    <div className="py-8 text-center text-slate-400 text-sm">No activity in this period</div>
                                ) : (
                                    <div className="relative pl-6 max-h-96 overflow-y-auto">
                                        <div className="absolute left-[7px] top-0 bottom-0 w-px bg-slate-200" />
                                        {timelineData.events.map((e, i) => (
                                            <div key={`e-${i}`} className="relative pb-4" data-testid={`timeline-event-${i}`}>
                                                <div className={`absolute -left-[22px] top-1 h-3 w-3 rounded-full ring-2 ring-white ${timelineDotColor(e.type)}`} />
                                                <div className="flex items-start gap-2">
                                                    <div className="mt-0.5">{timelineIcon(e.type)}</div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-slate-900 truncate">{e.title}</p>
                                                        {e.description && <p className="text-xs text-slate-500 truncate">{e.description}</p>}
                                                        <p className="text-[10px] text-slate-400 mt-0.5">{new Date(e.timestamp).toLocaleString()}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Coaching Session Dialog */}
            <Dialog open={!!coachDialog} onOpenChange={(o) => !o && setCoachDialog(null)}>
                <DialogContent className="rounded-none max-w-md">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>
                            <GraduationCap className="h-4 w-4 inline mr-2 text-emerald-600" />
                            Log Coaching — {coachDialog?.rm_name}
                        </DialogTitle>
                        <DialogDescription>Record a coaching session. The RM will be notified and the Alerts panel will show a Coached badge.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-3">
                        <div>
                            <Label className="text-xs font-bold uppercase text-slate-500">Session Type</Label>
                            <select value={coachForm.session_type} onChange={(e) => setCoachForm({...coachForm, session_type: e.target.value})} className="w-full rounded-none border border-slate-200 px-3 py-2 text-sm mt-1" data-testid="coach-session-type">
                                <option value="timeline_review">Timeline Review</option>
                                <option value="weekly_1on1">Weekly 1-on-1</option>
                                <option value="field_ride_along">Field Ride-Along</option>
                                <option value="training">Training</option>
                            </select>
                        </div>
                        <div>
                            <Label className="text-xs font-bold uppercase text-slate-500">Notes (optional)</Label>
                            <Textarea value={coachForm.notes} onChange={(e) => setCoachForm({...coachForm, notes: e.target.value})} className="rounded-none mt-1" rows={3} placeholder="What was discussed / action items..." data-testid="coach-notes" />
                        </div>
                        <div className="border border-red-200 bg-red-50/40 p-3">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <input type="checkbox" checked={coachForm.start_catchup} onChange={(e) => setCoachForm({...coachForm, start_catchup: e.target.checked})} className="h-4 w-4 accent-red-600" data-testid="catchup-toggle" />
                                <span className="text-sm font-bold text-red-800">Activate Catch-up Mode</span>
                            </label>
                            <p className="text-[11px] text-red-700 mt-1 ml-6">Sets a recovery deadline. Daily targets double until RM reaches 200% justification. Auto-escalates if missed.</p>
                            {coachForm.start_catchup && (
                                <div className="ml-6 mt-2 flex items-center gap-2">
                                    <Label className="text-[11px] text-slate-700">Recovery deadline:</Label>
                                    <input type="number" min="1" max="30" value={coachForm.catchup_days} onChange={(e) => setCoachForm({...coachForm, catchup_days: e.target.value})} className="w-16 rounded-none border border-slate-300 px-2 py-1 text-xs" data-testid="catchup-days" />
                                    <span className="text-[11px] text-slate-700">days</span>
                                </div>
                            )}
                        </div>
                        <div className="bg-emerald-50 border border-emerald-100 p-2 text-[11px] text-emerald-800">
                            The RM will receive an in-app notification and a "Coached" badge will appear on their alert card for the rest of the month.
                        </div>
                        <div className="flex justify-end gap-3">
                            <Button variant="outline" className="rounded-none" onClick={() => setCoachDialog(null)}>Cancel</Button>
                            <Button className="rounded-none bg-emerald-600 hover:bg-emerald-700" onClick={saveCoaching} disabled={coachSaving} data-testid="save-coach-btn">
                                {coachSaving ? 'Saving...' : 'Log Session'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* ============== Catalog Tab — Admin Products + Categories ============== */}
            {tab === 'catalog' && canManage && (
                <div className="space-y-6" data-testid="catalog-tab-content">
                    {/* Products card */}
                    <Card className="rounded-xl border-slate-200 shadow-sm">
                        <CardHeader className="border-b border-slate-200 py-4 flex flex-row items-center justify-between gap-3">
                            <div>
                                <CardTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                    <FileText className="h-4 w-4 text-[#2563eb]" /> Loan Products
                                </CardTitle>
                                <p className="text-xs text-slate-500 mt-1">Set commission % per product. RM picks the product on Disbursement → Gross Revenue auto-calculates.</p>
                            </div>
                            <Button size="sm" className="rounded-lg bg-[#2563eb] hover:bg-[#1d4ed8]" onClick={() => { setEditingProduct(null); setProductForm({ name: '', category: '', commission_rate: '', description: '' }); setProductDialog(true); }} data-testid="catalog-add-product-btn">
                                <Plus className="h-4 w-4 mr-1.5" />Add Product
                            </Button>
                        </CardHeader>
                        <CardContent className="p-0">
                            {catalogLoading ? (
                                <div className="p-8 text-center text-slate-400 text-sm">Loading products...</div>
                            ) : catalogData.products.length === 0 ? (
                                <div className="p-8 text-center text-slate-400 text-sm">No products yet. Click "Add Product" to create one.</div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b border-slate-200 bg-slate-50/80">
                                                <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Product</th>
                                                <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Category</th>
                                                <th className="text-right p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Commission %</th>
                                                <th className="text-left p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Notes</th>
                                                <th className="text-right p-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {catalogData.products.map(p => (
                                                <tr key={p._id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`catalog-product-row-${p._id}`}>
                                                    <td className="p-3 font-semibold text-slate-900">{p.name}</td>
                                                    <td className="p-3 text-slate-600">{p.category || '—'}</td>
                                                    <td className="p-3 text-right tabular-nums">
                                                        {(p.commission_rate || 0) > 0 ? (
                                                            <Badge className="rounded-md bg-emerald-100 text-emerald-700 font-bold">{p.commission_rate}%</Badge>
                                                        ) : (
                                                            <Badge className="rounded-md bg-amber-100 text-amber-700 font-bold">Manual</Badge>
                                                        )}
                                                    </td>
                                                    <td className="p-3 text-slate-500 text-xs max-w-xs truncate">{p.description || '—'}</td>
                                                    <td className="p-3 text-right">
                                                        <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-lg" onClick={() => editProduct(p)} data-testid={`catalog-edit-product-${p._id}`}>Edit</Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Categories card */}
                    <Card className="rounded-xl border-slate-200 shadow-sm">
                        <CardHeader className="border-b border-slate-200 py-4 flex flex-row items-center justify-between gap-3">
                            <div>
                                <CardTitle className="text-base font-bold flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                                    <Users className="h-4 w-4 text-[#2563eb]" /> Lead Categories
                                </CardTitle>
                                <p className="text-xs text-slate-500 mt-1">Used to bucket leads (e.g. Teacher Mitra, Shopkeeper, CA/Builder).</p>
                            </div>
                            <Button size="sm" className="rounded-lg bg-[#2563eb] hover:bg-[#1d4ed8]" onClick={() => { setCategoryForm({ name: '', description: '' }); setCategoryDialog(true); }} data-testid="catalog-add-category-btn">
                                <Plus className="h-4 w-4 mr-1.5" />Add Category
                            </Button>
                        </CardHeader>
                        <CardContent className="p-0">
                            {catalogData.categories.length === 0 ? (
                                <div className="p-8 text-center text-slate-400 text-sm">No categories yet.</div>
                            ) : (
                                <ul className="divide-y divide-slate-100">
                                    {catalogData.categories.map(c => (
                                        <li key={c._id} className="p-3 flex items-center justify-between hover:bg-slate-50" data-testid={`catalog-category-${c._id}`}>
                                            <div>
                                                <p className="font-semibold text-slate-900">{c.name}</p>
                                                {c.description && <p className="text-xs text-slate-500 mt-0.5">{c.description}</p>}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Product Dialog */}
            <Dialog open={productDialog} onOpenChange={(o) => { if (!o) { setProductDialog(false); setEditingProduct(null); } }}>
                <DialogContent className="rounded-2xl max-w-md" data-testid="product-dialog">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>{editingProduct ? 'Edit Product' : 'Add Loan Product'}</DialogTitle>
                        <DialogDescription>Set commission % so RMs see auto-calculated revenue when they disburse a loan with this product.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={saveProduct} className="space-y-4 mt-4">
                        <div>
                            <Label className="text-xs font-bold uppercase text-slate-500">Product Name</Label>
                            <Input value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} className="rounded-lg mt-1 bg-slate-50 border-slate-200" placeholder="e.g. Personal Loan" required data-testid="product-name-input" />
                        </div>
                        <div>
                            <Label className="text-xs font-bold uppercase text-slate-500">Category (optional)</Label>
                            <select value={productForm.category} onChange={(e) => setProductForm({ ...productForm, category: e.target.value })} className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm mt-1" data-testid="product-category-select">
                                <option value="">— None —</option>
                                {catalogData.categories.map(c => <option key={c._id} value={c.name}>{c.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label className="text-xs font-bold uppercase text-slate-500">Commission Rate (%)</Label>
                            <Input type="number" step="0.01" min="0" max="100" value={productForm.commission_rate} onChange={(e) => setProductForm({ ...productForm, commission_rate: e.target.value })} className="rounded-lg mt-1 bg-slate-50 border-slate-200" placeholder="e.g. 1.5" data-testid="product-commission-input" />
                            <p className="text-[11px] text-slate-500 mt-1.5">Gross Revenue = Loan Amount × this %. Set 0 to allow manual override on the lead.</p>
                        </div>
                        <div>
                            <Label className="text-xs font-bold uppercase text-slate-500">Description (optional)</Label>
                            <Textarea value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} className="rounded-lg mt-1 bg-slate-50 border-slate-200" rows={2} />
                        </div>
                        <div className="flex justify-end gap-3 pt-2">
                            <Button type="button" variant="outline" className="rounded-lg" onClick={() => { setProductDialog(false); setEditingProduct(null); }}>Cancel</Button>
                            <Button type="submit" className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg shadow-sm" data-testid="product-save-btn">{editingProduct ? 'Update' : 'Create'}</Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Category Dialog */}
            <Dialog open={categoryDialog} onOpenChange={(o) => !o && setCategoryDialog(false)}>
                <DialogContent className="rounded-2xl max-w-md" data-testid="category-dialog">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Chivo, sans-serif' }}>Add Lead Category</DialogTitle>
                        <DialogDescription>Categories are used as buckets in CRM leads + product grouping.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={saveCategory} className="space-y-4 mt-4">
                        <div>
                            <Label className="text-xs font-bold uppercase text-slate-500">Category Name</Label>
                            <Input value={categoryForm.name} onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} className="rounded-lg mt-1 bg-slate-50 border-slate-200" placeholder="e.g. Teacher Mitra" required data-testid="category-name-input" />
                        </div>
                        <div>
                            <Label className="text-xs font-bold uppercase text-slate-500">Description (optional)</Label>
                            <Textarea value={categoryForm.description} onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })} className="rounded-lg mt-1 bg-slate-50 border-slate-200" rows={2} />
                        </div>
                        <div className="flex justify-end gap-3 pt-2">
                            <Button type="button" variant="outline" className="rounded-lg" onClick={() => setCategoryDialog(false)}>Cancel</Button>
                            <Button type="submit" className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white rounded-lg shadow-sm" data-testid="category-save-btn">Create</Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default CRMDashboard;
